import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1533928298208-27ff66555d8d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const ShootingTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ShootingTypeCard = styled.div`
  position: relative;
  height: 350px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 1.5rem;
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  color: #0d9488;
  font-style: italic;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
`;

const FeaturesList = styled.div`
  margin: 4rem 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(13, 148, 136, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #0d9488;
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: #0d9488;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Shooting: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const shootingTypes = [
    {
      image: 'https://images.unsplash.com/photo-1533928298208-27ff66555d8d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'SHOOTING DI MODA' : 'FASHION SHOOTING',
      description: language === 'it' 
        ? 'Servizi fotografici per case di moda, stilisti e brand di abbigliamento in location esclusive e ben arredate.'
        : 'Photo shoots for fashion houses, designers and clothing brands in exclusive and well-furnished locations.'
    },
    {
      image: 'https://images.unsplash.com/photo-1610473078360-bd6930586b1e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'SPOT PUBBLICITARI' : 'ADVERTISING SPOTS',
      description: language === 'it' 
        ? 'Location per la realizzazione di spot pubblicitari e video promozionali per aziende e brand di ogni settore.'
        : 'Locations for the production of advertising spots and promotional videos for companies and brands in every sector.'
    },
    {
      image: 'https://images.unsplash.com/photo-1599500922460-417422f3856a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'SHOOTING FOTOGRAFICI' : 'PHOTOGRAPHIC SHOOTINGS',
      description: language === 'it' 
        ? 'Servizi fotografici per privati, fotografi professionisti e per occasioni speciali in ambienti unici e curati.'
        : 'Photo shoots for private individuals, professional photographers and for special occasions in unique and well-kept environments.'
    }
  ];
  
  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
      ),
      title: language === 'it' ? 'LOCATION UNICHE' : 'UNIQUE LOCATIONS',
      description: language === 'it' 
        ? 'Gli immobili offrono ambienti autentici, caratteristici e ben arredati, difficili da replicare in studio, donando naturalezza e personalità alle immagini.'
        : 'Properties offer authentic, characteristic and well-furnished environments, difficult to replicate in the studio, giving naturalness and personality to the images.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      ),
      title: language === 'it' ? 'VARIETÀ DI STILI' : 'VARIETY OF STYLES',
      description: language === 'it' 
        ? 'Dal moderno al rustico, dal minimalista al classico: accesso a una varietà di stili architettonici e di design per soddisfare diverse esigenze creative.'
        : 'From modern to rustic, from minimalist to classic: access to a variety of architectural and design styles to satisfy different creative needs.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      ),
      title: language === 'it' ? 'LUCE NATURALE' : 'NATURAL LIGHT',
      description: language === 'it' 
        ? 'Molti immobili offrono un\'illuminazione naturale eccellente attraverso ampie finestre e spazi aperti, un vantaggio significativo rispetto agli studi fotografici tradizionali.'
        : 'Many properties offer excellent natural lighting through large windows and open spaces, a significant advantage over traditional photography studios.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      title: language === 'it' ? 'PRIVACY E SICUREZZA' : 'PRIVACY AND SECURITY',
      description: language === 'it' 
        ? 'Gli immobili offrono privacy e sicurezza per shooting che richiedono riservatezza, come lanci di prodotti non ancora sul mercato o shooting con celebrità.'
        : 'Properties offer privacy and security for shoots that require confidentiality, such as launches of products not yet on the market or shoots with celebrities.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ),
      title: language === 'it' ? 'FLESSIBILITÀ DEGLI SPAZI' : 'FLEXIBILITY OF SPACES',
      description: language === 'it' 
        ? 'Gli immobili spesso offrono vari ambienti all\'interno della stessa proprietà (soggiorno, cucina, camera da letto, giardino), permettendo di realizzare shooting diversificati in un\'unica location.'
        : 'Properties often offer various environments within the same property (living room, kitchen, bedroom, garden), allowing for diversified shoots in a single location.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="17 1 21 5 17 9"></polyline>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
          <polyline points="7 23 3 19 7 15"></polyline>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
        </svg>
      ),
      title: language === 'it' ? 'COSTI COMPETITIVI' : 'COMPETITIVE COSTS',
      description: language === 'it' 
        ? 'Affittare un immobile per un shooting può essere più economico rispetto all\'affitto di uno studio fotografico professionale, soprattutto per progetti a budget limitato.'
        : 'Renting a property for a shoot can be cheaper than renting a professional photo studio, especially for limited budget projects.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Shooting | J-O-H' : 'Shooting | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Location esclusive per shooting di moda, spot pubblicitari e servizi fotografici professionali in ambienti unici e caratteristici' 
            : 'Exclusive locations for fashion shoots, commercials and professional photo shoots in unique and characteristic environments'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/pop-up">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>SHOOTING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Gli immobili per affitti brevi offrono location esclusive e versatili per shooting fotografici e video. Ambienti autentici, ben arredati e curati nei dettagli, rappresentano la cornice ideale per realizzare shooting di moda, spot pubblicitari e servizi fotografici professionali, garantendo risultati di alta qualità con un tocco di originalità e carattere.'
              : 'Short-term rental properties offer exclusive and versatile locations for photo and video shoots. Authentic environments, well furnished and attention to detail, represent the ideal setting for fashion shoots, commercials and professional photo shoots, guaranteeing high quality results with a touch of originality and character.'}
          </Description>
          
          <SectionTitle>
            {language === 'it' ? 'TIPOLOGIE DI SHOOTING' : 'TYPES OF SHOOTING'}
          </SectionTitle>
          
          <ShootingTypesGrid>
            {shootingTypes.map((type, index) => (
              <ShootingTypeCard key={index}>
                <CardImage src={type.image} alt={type.title} />
                <CardOverlay>
                  <CardTitle>{type.title}</CardTitle>
                  <CardDescription>{type.description}</CardDescription>
                </CardOverlay>
              </ShootingTypeCard>
            ))}
          </ShootingTypesGrid>
          
          <SectionTitle>
            {language === 'it' ? 'VANTAGGI DEGLI IMMOBILI COME LOCATION PER SHOOTING' : 'ADVANTAGES OF PROPERTIES AS LOCATIONS FOR SHOOTING'}
          </SectionTitle>
          
          <FeaturesList>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureItem>
            ))}
          </FeaturesList>
          
          <Description>
            {language === 'it' 
              ? 'L\'utilizzo di immobili per affitti brevi come location per shooting rappresenta una soluzione innovativa e vantaggiosa sia per i creativi che per i proprietari degli immobili. Offre accesso a spazi autentici, unici e ben curati che possono trasformare un semplice servizio fotografico in un progetto visivo di grande impatto, comunicando emozioni e storie attraverso ambienti reali e vissuti.'
              : 'The use of short-term rental properties as locations for shooting represents an innovative and advantageous solution for both creatives and property owners. It offers access to authentic, unique and well-kept spaces that can transform a simple photo shoot into a high-impact visual project, communicating emotions and stories through real and lived environments.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Shooting; 